import { useState, useEffect, useCallback } from "react";
import {
  CreditCard, Users, Building2, AlertTriangle, CheckCircle,
  Clock, Pause, XCircle, RefreshCw, ChevronRight, ChevronDown,
  ChevronUp, Shield, Zap, BookOpen, TrendingUp, Plus, Play,
  ToggleLeft, ToggleRight, Copy, Check, ExternalLink, Search, Info
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from "recharts";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const STATUS_CONFIG = {
  active: { label: "Actif", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: CheckCircle },
  pending_setup: { label: "En attente setup", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Clock },
  suspended: { label: "Suspendu", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: Pause },
  past_due: { label: "Impaye", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: AlertTriangle },
  canceled: { label: "Annule", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", icon: XCircle },
  inactive: { label: "Inactif", color: "text-slate-500", bg: "bg-slate-500/10", border: "border-slate-500/30", icon: XCircle },
};

const MODULE_CONFIG = {
  commercial: { label: "Commercial", icon: TrendingUp, color: "text-blue-400" },
  compta: { label: "Compta", icon: CreditCard, color: "text-emerald-400" },
  marketing: { label: "Marketing", icon: Zap, color: "text-purple-400" },
  formation: { label: "Formation", icon: BookOpen, color: "text-amber-400" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactive;
  const Icon = cfg.icon;
  return (
    <span data-testid={`billing-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function ModuleBadge({ moduleId, enabled }) {
  const cfg = MODULE_CONFIG[moduleId] || { label: moduleId, icon: Shield, color: "text-slate-400" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${enabled ? `${cfg.color} bg-slate-800` : "text-slate-600 bg-slate-900 line-through"}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function KPICard({ label, value, icon: Icon, color = "text-white", subtext }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONBOARD MODAL
// ══════════════════════════════════════════════════════════════
function OnboardModal({ onClose, onSuccess, token }) {
  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [includeAudit, setIncludeAudit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/billing/admin/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_name: companyName, contact_email: contactEmail, include_audit: includeAudit }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setResult(data);
      } else {
        alert(data.detail || "Erreur onboarding");
      }
    } catch (err) {
      alert("Erreur reseau: " + err.message);
    }
    setLoading(false);
  };

  const copyUrl = () => {
    if (result?.checkout_url) {
      navigator.clipboard.writeText(result.checkout_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <h3 data-testid="onboard-modal-title" className="text-lg font-bold flex items-center gap-2">
          <Plus className="w-5 h-5 text-emerald-400" /> Onboarder un nouveau client
        </h3>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Nom de la societe *</label>
              <input
                data-testid="onboard-company-name"
                type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="Ex: SANI ADAPT'"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Email du contact *</label>
              <input
                data-testid="onboard-contact-email"
                type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="admin@client.fr"
              />
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setIncludeAudit(!includeAudit)} data-testid="onboard-audit-toggle">
                {includeAudit ? <ToggleRight className="w-8 h-8 text-emerald-400" /> : <ToggleLeft className="w-8 h-8 text-slate-500" />}
              </button>
              <div>
                <p className="text-sm">Inclure Audit & Consulting</p>
                <p className="text-xs text-slate-500">+2 000 EUR (one-time)</p>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <p className="text-sm text-slate-300">
                Montant setup : <span className="font-bold text-white">{includeAudit ? "2 990" : "990"} EUR</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">Un lien Stripe Checkout sera genere pour le client.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
                Annuler
              </button>
              <button
                data-testid="onboard-submit-btn"
                type="submit" disabled={loading}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {loading ? "Creation..." : "Creer & generer lien"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
              <p className="text-emerald-400 font-semibold flex items-center gap-2">
                <CheckCircle className="w-5 h-5" /> Tenant cree avec succes
              </p>
              <p className="text-sm text-slate-300 mt-2">
                <strong>{result.company_name}</strong> — {result.billing_status}
              </p>
            </div>

            {result.checkout_url ? (
              <div>
                <label className="text-sm text-slate-400 block mb-1">Lien Stripe Checkout :</label>
                <div className="flex gap-2">
                  <input readOnly value={result.checkout_url} className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs font-mono" />
                  <button onClick={copyUrl} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg" data-testid="copy-checkout-url">
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Copiez ce lien et envoyez-le au client.</p>
              </div>
            ) : (
              <p className="text-xs text-amber-400">Lien Checkout non disponible (Stripe non configure).</p>
            )}

            <button onClick={() => { onSuccess(); onClose(); }} className="w-full py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TENANT DETAIL PANEL
// ══════════════════════════════════════════════════════════════
function TenantDetailPanel({ tenantId, token, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/billing/admin/dashboard/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setDetail(await resp.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tenantId, token]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (endpoint, body = {}) => {
    setActionLoading(endpoint);
    try {
      const resp = await authFetch(`${API}/billing/admin/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (resp.ok) await load();
      else {
        const err = await resp.json();
        alert(err.detail || "Erreur");
      }
    } catch (e) { alert(e.message); }
    setActionLoading("");
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
    </div>
  );

  if (!detail) return (
    <div className="text-center py-10 text-slate-500">Tenant introuvable</div>
  );

  const modules = detail.modules_enabled || {};

  return (
    <div data-testid="billing-tenant-detail" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg" data-testid="billing-detail-back">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold">{detail.name || tenantId}</h2>
          <p className="text-sm text-slate-400 font-mono">{tenantId}</p>
        </div>
        <StatusBadge status={detail.billing_status} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Utilisateurs actifs" value={detail.active_users_this_cycle || 0} icon={Users} color="text-blue-400" subtext="Ce cycle (30j)" />
        <KPICard label="Montant projete" value={`${(detail.projected_amount || 0).toLocaleString("fr-FR")} EUR`} icon={CreditCard} color="text-emerald-400" subtext={`Tranche ${detail.projected_tier || "-"}`} />
        <KPICard label="Seats en cours" value={detail.current_seats || 0} icon={Users} color="text-purple-400" subtext={`Tier: ${detail.current_tier || "-"}`} />
        <KPICard label="Setup" value={detail.setup_paid ? "Paye" : "Non paye"} icon={detail.setup_paid ? CheckCircle : Clock} color={detail.setup_paid ? "text-emerald-400" : "text-amber-400"} subtext={detail.audit_paid ? "Audit inclus" : ""} />
      </div>

      {/* Modules */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400" /> Modules
        </h3>
        <div className="space-y-3">
          {Object.entries(MODULE_CONFIG).map(([modId, cfg]) => {
            const enabled = modules[modId] === true;
            const Icon = cfg.icon;
            const isCommercial = modId === "commercial";
            return (
              <div key={modId} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${enabled ? cfg.color : "text-slate-600"}`} />
                  <div>
                    <p className={`text-sm font-medium ${enabled ? "text-white" : "text-slate-500"}`}>{cfg.label}</p>
                    {isCommercial && <p className="text-xs text-slate-500">Inclus dans le socle</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${enabled ? "text-emerald-400" : "text-slate-600"}`}>
                    {enabled ? "Active" : "Desactive"}
                  </span>
                  {!isCommercial && (
                    <button
                      data-testid={`module-toggle-${modId}`}
                      onClick={() => doAction(enabled ? "module/disable" : "module/enable", { tenant_id: tenantId, module_id: modId })}
                      disabled={!!actionLoading}
                      className="p-1 hover:bg-slate-700 rounded"
                    >
                      {enabled ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6 text-slate-500" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stripe Info */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-purple-400" /> Stripe
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Customer ID</span>
            <p className="font-mono text-xs">{detail.stripe_customer_id || "—"}</p>
          </div>
          <div>
            <span className="text-slate-500">Subscription ID</span>
            <p className="font-mono text-xs">{detail.stripe_subscription_id || "—"}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" /> Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          {detail.billing_status === "pending_setup" && (
            <button
              data-testid="billing-force-activate"
              onClick={() => doAction(`force-activate/${tenantId}`, {})}
              disabled={!!actionLoading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-sm font-semibold flex items-center gap-2"
            >
              {actionLoading === `force-activate/${tenantId}` ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Force Activer
            </button>
          )}
          {detail.billing_status === "active" && (
            <button
              data-testid="billing-suspend"
              onClick={() => {
                const reason = prompt("Raison de la suspension :");
                if (reason !== null) doAction("suspend", { tenant_id: tenantId, reason });
              }}
              disabled={!!actionLoading}
              className="px-4 py-2 bg-red-600/80 hover:bg-red-600 disabled:opacity-50 rounded-lg text-sm font-semibold flex items-center gap-2"
            >
              <Pause className="w-4 h-4" /> Suspendre
            </button>
          )}
          {detail.billing_status === "suspended" && (
            <button
              data-testid="billing-reactivate"
              onClick={() => doAction("reactivate", { tenant_id: tenantId })}
              disabled={!!actionLoading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-sm font-semibold flex items-center gap-2"
            >
              {actionLoading === "reactivate" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Reactiver
            </button>
          )}
          <button
            onClick={load}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraichir
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MRR CHART
// ══════════════════════════════════════════════════════════════
function MrrTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs shadow-xl">
      <p className="font-semibold text-white mb-1">{label}</p>
      <p className="text-emerald-400">MRR: {(d.total_mrr || 0).toLocaleString("fr-FR")} EUR</p>
      <p className="text-blue-400">Seats: {(d.seats_mrr || 0).toLocaleString("fr-FR")} EUR</p>
      <p className="text-purple-400">Modules: {(d.modules_mrr || 0).toLocaleString("fr-FR")} EUR</p>
      <p className="text-slate-400 mt-1">{d.active_tenants || 0} tenants actifs</p>
    </div>
  );
}

function MrrChart({ token }) {
  const [series, setSeries] = useState([]);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/billing/admin/mrr-timeseries?months=${months}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSeries(data.series || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, months]);

  useEffect(() => { load(); }, [load]);

  const currentMrr = series.length > 0 ? series[series.length - 1].total_mrr : 0;
  const prevMrr = series.length > 1 ? series[series.length - 2].total_mrr : 0;
  const delta = prevMrr > 0 ? Math.round(((currentMrr - prevMrr) / prevMrr) * 100) : 0;

  return (
    <div data-testid="mrr-chart" className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          <div>
            <h3 className="font-semibold text-sm">MRR — Monthly Recurring Revenue</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-2xl font-bold text-emerald-400">{currentMrr.toLocaleString("fr-FR")} EUR</span>
              {delta !== 0 && (
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${delta > 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                  {delta > 0 ? "+" : ""}{delta}%
                </span>
              )}
            </div>
          </div>
          <div className="ml-3 group relative">
            <Info className="w-4 h-4 text-slate-500 cursor-help" />
            <div className="hidden group-hover:block absolute left-6 top-0 z-10 w-64 bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs text-slate-300 shadow-xl">
              <p className="font-semibold text-white mb-1">Source: Projection interne (Option A)</p>
              <p>MRR = tenants actifs x (seats x prix tier + modules actives).</p>
              <p className="mt-1 text-slate-500">Exclut les frais one-time (setup, audit).</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[6, 12].map((m) => (
            <button
              key={m}
              data-testid={`mrr-period-${m}`}
              onClick={() => setMonths(m)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${months === m ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-400 hover:bg-slate-600"}`}
            >
              {m} mois
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-5 h-5 animate-spin text-slate-500" />
        </div>
      ) : series.length < 2 ? (
        <div className="flex items-center justify-center h-40 text-slate-500 text-sm">
          <p>Pas assez de donnees historiques. Le graphique apparaitra apres 2 mois de snapshots.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={series} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
            <Tooltip content={<MrrTooltipContent />} />
            <Area type="monotone" dataKey="total_mrr" stroke="#10b981" strokeWidth={2} fill="url(#mrrGradient)" dot={{ fill: "#10b981", r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {/* Single-point summary when only current month exists */}
      {!loading && series.length === 1 && (
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-blue-400">{series[0].seats_mrr?.toLocaleString("fr-FR")} EUR</p>
            <p className="text-xs text-slate-500">Seats MRR</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-purple-400">{series[0].modules_mrr?.toLocaleString("fr-FR")} EUR</p>
            <p className="text-xs text-slate-500">Modules MRR</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-sm font-bold text-white">{series[0].active_tenants}</p>
            <p className="text-xs text-slate-500">Tenants actifs</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function BillingDashboard({ authUser }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showOnboard, setShowOnboard] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  const token = authUser?.session_token || authUser?.token || "";

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/billing/admin/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setTenants(data.tenants || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Stats
  const totalTenants = tenants.length;
  const activeCount = tenants.filter((t) => t.billing_status === "active").length;
  const pendingCount = tenants.filter((t) => t.billing_status === "pending_setup").length;
  const suspendedCount = tenants.filter((t) => ["suspended", "past_due", "canceled"].includes(t.billing_status)).length;
  const totalSeats = tenants.reduce((s, t) => s + (t.current_seats || 0), 0);

  // MRR estimation
  const estimatedMRR = tenants.reduce((sum, t) => {
    if (t.billing_status !== "active") return sum;
    const seats = Math.max(t.current_seats || 1, 1);
    let pricePerSeat = 199;
    if (seats > 50) pricePerSeat = 129;
    else if (seats > 10) pricePerSeat = 149;
    else if (seats > 5) pricePerSeat = 169;
    let modulesTotal = 0;
    const mods = t.modules_enabled || {};
    if (mods.compta) modulesTotal += 249;
    if (mods.marketing) modulesTotal += 99;
    if (mods.formation) modulesTotal += 199;
    return sum + seats * pricePerSeat + modulesTotal;
  }, 0);

  // Filter & sort
  const filtered = tenants
    .filter((t) => {
      if (statusFilter !== "all" && t.billing_status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (t.name || "").toLowerCase().includes(q) || (t.tenant_id || "").toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") cmp = (a.name || "").localeCompare(b.name || "");
      else if (sortField === "status") cmp = (a.billing_status || "").localeCompare(b.billing_status || "");
      else if (sortField === "seats") cmp = (a.current_seats || 0) - (b.current_seats || 0);
      return sortAsc ? cmp : -cmp;
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  // If a tenant is selected, show detail
  if (selectedTenant) {
    return <TenantDetailPanel tenantId={selectedTenant} token={token} onBack={() => { setSelectedTenant(null); loadOverview(); }} />;
  }

  return (
    <div data-testid="billing-dashboard" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Facturation SaaS</h2>
              <p className="text-slate-400 text-sm">Gestion des abonnements, modules et onboarding</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={loadOverview} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </button>
            <button
              data-testid="billing-onboard-btn"
              onClick={() => setShowOnboard(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg flex items-center gap-2 text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Onboarder client
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPICard label="Total tenants" value={totalTenants} icon={Building2} color="text-white" />
        <KPICard label="Actifs" value={activeCount} icon={CheckCircle} color="text-emerald-400" />
        <KPICard label="En attente" value={pendingCount} icon={Clock} color="text-amber-400" />
        <KPICard label="Suspendus" value={suspendedCount} icon={AlertTriangle} color="text-red-400" />
        <KPICard label="MRR estime" value={`${estimatedMRR.toLocaleString("fr-FR")} EUR`} icon={TrendingUp} color="text-emerald-400" subtext={`${totalSeats} seats`} />
      </div>

      {/* MRR Chart */}
      <MrrChart token={token} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="billing-search"
            type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher tenant..."
            className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <select
          data-testid="billing-status-filter"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="pending_setup">En attente</option>
          <option value="suspended">Suspendu</option>
          <option value="canceled">Annule</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun tenant trouve</p>
        </div>
      ) : (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50 text-left">
                <th className="px-4 py-3 cursor-pointer hover:text-white text-slate-400" onClick={() => toggleSort("name")}>
                  <span className="flex items-center gap-1">
                    Societe {sortField === "name" && (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
                <th className="px-4 py-3 cursor-pointer hover:text-white text-slate-400" onClick={() => toggleSort("status")}>
                  <span className="flex items-center gap-1">
                    Statut {sortField === "status" && (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
                <th className="px-4 py-3 cursor-pointer hover:text-white text-slate-400" onClick={() => toggleSort("seats")}>
                  <span className="flex items-center gap-1">
                    Seats {sortField === "seats" && (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
                <th className="px-4 py-3 text-slate-400">Modules</th>
                <th className="px-4 py-3 text-slate-400">Setup</th>
                <th className="px-4 py-3 text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.tenant_id}
                  data-testid={`billing-row-${t.tenant_id}`}
                  className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors cursor-pointer"
                  onClick={() => setSelectedTenant(t.tenant_id)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{t.name || "—"}</p>
                    <p className="text-xs text-slate-500 font-mono">{t.tenant_id}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={t.billing_status} /></td>
                  <td className="px-4 py-3">
                    <span className="text-white font-semibold">{t.current_seats || 0}</span>
                    {t.current_tier && <span className="text-xs text-slate-500 ml-1">({t.current_tier})</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(t.modules_enabled || {}).map(([modId, enabled]) => (
                        <ModuleBadge key={modId} moduleId={modId} enabled={enabled} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {t.setup_paid ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-600" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-500 inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pricing Summary */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
          <TrendingUp className="w-4 h-4 text-emerald-400" /> Grille tarifaire
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-white">199 EUR</p>
            <p className="text-xs text-slate-500">1-5 seats/mois</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-white">169 EUR</p>
            <p className="text-xs text-slate-500">6-10 seats/mois</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-white">149 EUR</p>
            <p className="text-xs text-slate-500">11-50 seats/mois</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-white">129 EUR</p>
            <p className="text-xs text-slate-500">50+ seats/mois</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
          <span>Setup: 990 EUR</span>
          <span>Audit: 2 000 EUR</span>
          <span>Compta: 249 EUR/mois</span>
          <span>Marketing: 99 EUR/mois</span>
          <span>Formation: 199 EUR/mois</span>
        </div>
      </div>

      {/* Onboard Modal */}
      {showOnboard && (
        <OnboardModal
          token={token}
          onClose={() => setShowOnboard(false)}
          onSuccess={loadOverview}
        />
      )}
    </div>
  );
}
